import React, { useEffect, useState, useRef } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaDownload, FaFilter } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { hasPermission } from "../../utils/permissionHelper";

const initialFilters = {
  clusterId: '',
  chapterId: '',
  startDate: '',
  endDate: ''
};
const initialDropdowns = {
  clusters: [],
  chapters: []
};

const Eventindex = () => {
  const [events, setEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [dropdowns, setDropdowns] = useState(initialDropdowns);
  const [allChapters, setAllChapters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  const qrRef = useRef();
  const user = JSON.parse(localStorage.getItem("user")); // logged in user

  useEffect(() => {
    if (hasPermission(user, "Event", "Read")) {
      fetchDropdownOptions();
      fetchEvents(currentPage, searchQuery, filters);
    }
    // eslint-disable-next-line
  }, [currentPage, searchQuery, filters]);

  const handleDownloadQRCode = async () => {
    if (!qrRef.current) return;
    // Use html2canvas to capture the QR code div
    const canvas = await html2canvas(qrRef.current, { backgroundColor: '#fff', scale: 1 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [250, 320] });
    // Add QR image
    pdf.addImage(imgData, 'PNG', 65, 40, 150, 100);
    // Add event name below QR
    if (selectedEvent?.Name) {
      pdf.setFontSize(14);
      pdf.text(selectedEvent.Name, 125, 230, { align: 'center' });
    }
    pdf.save(`${selectedEvent?.Name || 'QRCode'}.pdf`);
  };

  const handleDirectDownloadQR = async (event) => {
    try {
      // Create a temporary div to render QR code for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = '#fff';
      tempDiv.style.padding = '20px';
      tempDiv.style.width = '200px';
      tempDiv.style.height = '200px';
      document.body.appendChild(tempDiv);

      // Create QR code in the temp div using qrcode.react
      const { createRoot } = require('react-dom/client');
      const root = createRoot(tempDiv);
      
      await new Promise((resolve) => {
        root.render(
          React.createElement(QRCodeSVG, {
            value: event._id,
            size: 180,
            level: "M",
            includeMargin: true
          })
        );
        setTimeout(resolve, 100); // Wait for render
      });

      // Use html2canvas to capture the QR code
      const canvas = await html2canvas(tempDiv, { backgroundColor: '#fff', scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF using same format as modal
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [250, 320] });
      pdf.addImage(imgData, 'PNG', 35, 40, 180, 180);
      
      // Add event name
      if (event.name) {
        pdf.setFontSize(14);
        pdf.text(event.name, 125, 240, { align: 'center' });
      }
      
      // Add date and time info
      pdf.setFontSize(10);
      pdf.text(`Date: ${new Date(event.startDate).toLocaleDateString()}`, 125, 260, { align: 'center' });
      pdf.text(`Time: ${new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, 125, 275, { align: 'center' });
      
      pdf.save(`${event.name || 'Event'}_QRCode.pdf`);
      
      // Clean up
      root.unmount();
      document.body.removeChild(tempDiv);
      toastSuccess('QR Code PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating QR PDF:', error);
      toastError('Failed to download QR Code PDF');
    }
  };

  // Fetch dropdown options for filters (same as user page)
  const fetchDropdownOptions = async () => {
    try {
      const [clustersRes, chaptersRes] = await Promise.all([
        axios.get('/admin/api/clusters/all'),
        axios.get('/admin/api/chapters/all')
      ]);
      setDropdowns({
        clusters: clustersRes.data?.cluster?.map((c) => ({ value: c._id, label: c.name })) || [],
        chapters: chaptersRes.data?.chapter?.map((ch) => ({
          value: ch._id,
          label: ch.name,
          clusterId: ch.cluster?._id || ch.cluster // support both object and id
        })) || []
      });
      setAllChapters(chaptersRes.data?.chapter?.map((ch) => ({
        value: ch._id,
        label: ch.name,
        clusterId: ch.cluster?._id || ch.cluster
      })) || []);
    } catch (err) {
      toastError('Failed to load filter dropdowns');
    }
  };

  // Filter chapters by selected clusterId
  const filteredChapters = React.useMemo(() => {
    if (!filters.clusterId) return allChapters;
    return allChapters.filter(ch => ch.clusterId === filters.clusterId);
  }, [allChapters, filters.clusterId]);

  // Update fetchEvents to use clusterId/chapterId as in user page
  const fetchEvents = async (page = 1, search = '', filtersData = filters) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        search
      });
      // Use API param names: cluster and chapter
      if (filtersData.clusterId) params.append('cluster', filtersData.clusterId);
      if (filtersData.chapterId) params.append('chapter', filtersData.chapterId);
      if (filtersData.startDate) params.append('startDate', filtersData.startDate);
      if (filtersData.endDate) params.append('endDate', filtersData.endDate);

      const res = await axios.get(`/api/event?${params.toString()}`);
      const { events = [], totalCount = 0 } = res.data;
      setEvents(events);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch events');
      console.error(err);
    }
  };

  // Handle filter changes (reset chapterId if clusterId changes)
  const handleFilterChange = (field, value) => {
    if (field === 'clusterId') {
      setFilters(prev => {
        const updated = { ...prev, clusterId: value, chapterId: '' };
        setCurrentPage(1);
        fetchEvents(1, searchQuery, updated);
        return updated;
      });
    } else {
      setFilters(prev => {
        const updated = { ...prev, [field]: value };
        setCurrentPage(1);
        fetchEvents(1, searchQuery, updated);
        return updated;
      });
    }
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Open modal and set selected event
  const openQrModal = (event) => {
    setSelectedEvent(event);
    setQrModalOpen(true);
  };

  // Close modal
  const closeQrModal = () => {
    setQrModalOpen(false);
    setSelectedEvent(null);
  };

  // Add handleDelete function
  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/event/${id}`);
      toastSuccess('Event deleted successfully!');
      setCurrentPage(1);
      fetchEvents(1, searchQuery, filters);
    } catch (err) {
      toastError('Failed to delete event');
      console.error(err);
    }
  };

  if (!hasPermission(user, "Event", "Read")) {
    return (
      <div className="container py-4 text-center">
        <p className="text-danger fw-bold">
          🚫 You don’t have permission to view Events.
        </p>
      </div>
    );
  }

  return (
    <div className="container mt-2">
      {/* Header and Search */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-dark"> events</h3>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control"
            style={{ width: 200 }}
            placeholder="Search Event..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              backgroundColor: showFilters ? '#C44C23' : 'transparent',
              color: showFilters ? '#fff' : '#C44C23',
              border: '1px solid #C44C23',
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
          </button>
        </div>
      </div>

      {/* --- Filter UI --- */}
      {showFilters && (
        <div className="card mb-2 p-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label mb-1">Cluster</label>
              <select
                className="form-select"
                value={filters.clusterId}
                onChange={e => handleFilterChange('clusterId', e.target.value)}
              >
                <option value="">All Clusters</option>
                {dropdowns.clusters.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1">Chapter</label>
              <select
                className="form-select"
                value={filters.chapterId}
                onChange={e => handleFilterChange('chapterId', e.target.value)}
              >
                <option value="">All Chapters</option>
                {filteredChapters.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.startDate}
                onChange={e => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.endDate}
                onChange={e => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>
      )}

      {/* Events Table */}
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Events</h5>
          {hasPermission(user, "Event", "Create") && (
          <Link to="/event/create" className="btn btn-sm btn-success">
            + Add New Event
          </Link>
        )}
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2">Sr no.</th>
                <th className="py-1 px-2">Meeting Id</th>
                <th className="py-1 px-2">Chapter</th>
                <th className="py-1 px-2">Event Name</th>
                <th className="py-1 px-2">Event Date</th>
                <th className="py-1 px-2">Event Time</th>
                <th className="py-1 px-2">Venue</th>
                <th className="py-1 px-2">Qr Code</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length > 0 ? (
                events.map((e, idx) => (
                  <tr key={e._id}>
                    <td>{(currentPage - 1) * limit + idx + 1}</td>
                    <td>{e.eventId || '-'}</td>
                    <td>{e.chapter?.map(ch => ch.name).join(', ') || 'N/A'}</td>
                    <td>{e.name}</td>
                    <td>{new Date(e.startDate).toLocaleDateString()}</td>
                    <td>{new Date(e.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{e.venue?.name || e.meetingVenue?.name || 'N/A'}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <span style={{ cursor: 'pointer' }} onClick={() => openQrModal(e)} title="View QR Code">
                          <QRCodeSVG
                            value={e._id}
                            size={25}
                            level="M"
                            includeMargin={false}
                          />
                        </span>
                        <button
                          className="btn btn-sm p-0 text-primary-custom"
                          onClick={() => handleDirectDownloadQR(e)}
                          title="Download QR Code PDF"
                        >
                          <FaDownload size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                     {hasPermission(user, "Event", "Read") && (
                      <Link to={`/event/view/${e._id}`}
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="View"
                      >
                        <FaEye size={14} />
                      </Link>
                    )}
                    {hasPermission(user, "Event", "Edit") && (
                      <Link
                        to={`/event/edit/${e._id}`}
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                      >
                        <FaEdit size={14} />
                      </Link>
                    )}
                      {hasPermission(user, "Event", "Delete") && (
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                        title="Delete"
                        onClick={() => handleDelete(e._id)}
                      >
                        <FaTrash size={14} />
                      </button>
                    )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">No event found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>
              {(() => {
                const pages = [];
                const maxVisiblePages = 3;
                pages.push(
                  <button
                    key={1}
                    className={`btn btn-sm page-btn ${currentPage === 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </button>
                );
                if (currentPage > maxVisiblePages + 1) {
                  pages.push(
                    <span key="ellipsis1" className="px-2">...</span>
                  );
                }
                const startPage = Math.max(2, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);
                for (let page = startPage; page <= endPage; page++) {
                  if (page !== 1 && page !== totalPages) {
                    pages.push(
                      <button
                        key={page}
                        className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }
                }
                if (currentPage < totalPages - maxVisiblePages) {
                  pages.push(
                    <span key="ellipsis2" className="px-2">...</span>
                  );
                }
                if (totalPages > 1) {
                  pages.push(
                    <button
                      key={totalPages}
                      className={`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }
                return pages;
              })()}
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      {/* QR Code Modal */}
      {qrModalOpen && selectedEvent && (
      <div
        className="modal fade show"
        style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1050,
        }}
        onClick={closeQrModal}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '400px' }}
        >
          <div className="modal-content p-3">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">Event QR Code</h5>
              <button className="btn btn-sm btn-primary" onClick={handleDownloadQRCode}>
                Download
              </button>
            </div>

            {/* Body */}
            <div className="modal-body text-center">
              <h6 className="mb-3">{selectedEvent.Name}</h6>

             <div ref={qrRef}>
              <QRCodeSVG
                value={selectedEvent._id}
                size={180}
                level="M"
                includeMargin={true}
              />
            </div>

              <div className="container text-center mt-4">
                {/* Labels Row */}
                <div className="row fw-bold mb-1">
                  <div className="col-6">Date</div>
                  <div className="col-6">Time</div>
                </div>

                {/* Values Row */}
                <div className="row">
                  <div className="col-6">
                    {new Date(selectedEvent.startDate).toLocaleDateString()}
                  </div>
                  <div className="col-6 p-0">
                    {new Date(selectedEvent.startDate).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {selectedEvent.endDate
                      ? ` to ${new Date(selectedEvent.endDate).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeQrModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default Eventindex;
